import { Box, HStack, Text, Button } from '@chakra-ui/react';
import {
  useGetWorksheetUserAnswersById,
  WorksheetAnswerStatus,
} from 'hooks/api/backend/worksheets/useWorksheets';
import { ReactElement } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { PageContext } from 'components/common/PageContext';
import { QueryErrorState } from 'components/common/QueryErrorState';
import { PageSpinner } from 'components/atoms/PageSpinner';
import '@blocknote/mantine/style.css';
import 'styles/worksheets/styles.css';
import { FileIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { WorksheetAnswer } from 'components/worksheet/Review/WorksheetAnswer';
import { ProtectedRoutes } from 'types/Routes';

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

export const WorksheetUserAnswerReview = (): ReactElement | null => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewType = searchParams.get('viewType') || '';
  const isWebview = viewType === 'webview';
  const { data: worksheetData, isLoading, isError, error, refetch } =
    useGetWorksheetUserAnswersById(id);

  if (isLoading) {
    return <PageSpinner />;
  }

  if (isError) {
    return <QueryErrorState error={error} onRetry={() => refetch()} />;
  }

  if (worksheetData === null) {
    return null;
  }

  const handleContinueEditing = (): void => {
    if (!worksheetData) return;

    if (isWebview && window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'CONTINUE_EDITING',
          worksheetId: worksheetData.worksheetId,
        })
      );
    } else {
      navigate(`${ProtectedRoutes.WORKSHEET_SESSION}/${worksheetData.worksheetId}`);
    }
  };

  const isInProgress = worksheetData?.status === WorksheetAnswerStatus.IN_PROGRESS;

  return (
    <PageContext
      hasBackButton
      title={worksheetData?.title || 'Arbeitsblatt'}
      headTitle={worksheetData?.title || 'Arbeitsblatt'}
    >
      <Box w="full" py="4xl" flexGrow={1}>
        <Box
          bg="background.primary"
          p="4xl"
          height="100%"
          flexGrow={1}
          width={{ base: '100%', md: '800px' }}
          maxWidth="100%"
          borderRadius={{ md: '2xl' }}
        >
          <HStack justifyContent="space-between" alignItems="center" mb="l" flexWrap="wrap" gap="m">
            <HStack gap="10px">
              <Box
                boxSize="34px"
                bgColor="tag.background.green"
                display="grid"
                placeItems="center"
                borderRadius="5px"
                flexShrink={0}
              >
                <NovaIcon icon={FileIcon} size="l" color="icon.accent" aria-hidden />
              </Box>
              <Text textStyle="h2.medium">{worksheetData?.title}</Text>
            </HStack>
            {isInProgress && (
              <Button onClick={handleContinueEditing} variant="primary">
                Bearbeitung fortsetzen
              </Button>
            )}
          </HStack>
          {worksheetData && (
            <WorksheetAnswer
              worksheetId={worksheetData?.worksheetId}
              participantWorksheetData={worksheetData}
            />
          )}
        </Box>
      </Box>
    </PageContext>
  );
};
